import React, { useState, useEffect, useRef, useCallback } from 'react';
import { notificationService } from '../utils/notifications';
import { 
  getNotifications, 
  getNotificationsCount, 
  getMembreNotifications,
  getMembreNotificationsCount,
  getRecolteurNotifications,
  getRecolteurNotificationsCount,
  marquerNotificationLue, 
  marquerToutesLues,
  marquerMembreNotificationLue,
  marquerToutesMembreNotificationsLues,
  marquerRecolteurNotificationLue,
  marquerToutesRecolteurNotificationsLues
} from '../services/api';
import './NotificationBell.css';

// Convertir le type de notification de l'API en type pour l'affichage
const getNotificationType = (type) => {
  switch (type) {
    case 'membre_creer':
      return 'success';
    case 'paiement_enregistre':
    case 'cotisation_payee':
      return 'info';
    default:
      return 'info';
  }
};

const POLL_INTERVAL_MS = 180000;  // 3 minutes (réduit pour éviter 429 / QUIC)
const RETRY_DELAY_BASE_MS = 45000;  // 45s après 429/erreur réseau
const MIN_RELOAD_DEBOUNCE_MS = 60000;  // min 1 entre deux rechargements déclenchés par toast

const NotificationBell = () => {
  const [notifications, setNotifications] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const panelRef = useRef(null);
  const pollingIntervalRef = useRef(null);
  const retryTimeoutRef = useRef(null);
  const retryCountRef = useRef(0);
  const loadInProgressRef = useRef(false);
  const lastLoadFromSubscribeRef = useRef(0);

  // Déterminer le type d'utilisateur connecté basé sur l'URL actuelle
  const getUserType = useCallback(() => {
    const currentPath = window.location.pathname;
    const membre = JSON.parse(localStorage.getItem('membre_user') || '{}');
    const admin = JSON.parse(localStorage.getItem('admin_user') || '{}');
    const recolteur = JSON.parse(localStorage.getItem('recolteur_user') || '{}');
    const finances = JSON.parse(localStorage.getItem('finances_user') || '{}');
    const gerer = JSON.parse(localStorage.getItem('gerer_user') || '{}');
    
    // Déterminer le type d'utilisateur basé sur l'URL actuelle pour éviter les conflits
    if (currentPath.includes('/finances')) {
      return finances.id ? { type: 'finances', data: finances } : { type: null, data: null };
    }
    if (currentPath.includes('/gerer') || currentPath.includes('/famille')) {
      return gerer.id ? { type: 'gerer', data: gerer } : { type: null, data: null };
    }
    if (currentPath.includes('/recolteur')) {
      return recolteur.id ? { type: 'recolteur', data: recolteur } : { type: null, data: null };
    }
    if (currentPath.includes('/membre')) {
      return membre.numero_matricule ? { type: 'membre', data: membre } : { type: null, data: null };
    }
    if (currentPath.includes('/admin') || currentPath.includes('/dashboard')) {
      return admin.id ? { type: 'admin', data: admin } : { type: null, data: null };
    }
    
    // Fallback: essayer de détecter par les données stockées
    if (admin.id) return { type: 'admin', data: admin };
    if (finances.id) return { type: 'finances', data: finances };
    if (gerer.id) return { type: 'gerer', data: gerer };
    if (recolteur.id) return { type: 'recolteur', data: recolteur };
    if (membre.numero_matricule) return { type: 'membre', data: membre };
    return { type: null, data: null };
  }, []);

  // Charger les notifications depuis l'API (une seule requête à la fois, évite 429/QUIC)
  const loadNotifications = useCallback(async (fromSubscribe = false) => {
    if (loadInProgressRef.current) return;
    if (fromSubscribe) {
      const now = Date.now();
      if (now - lastLoadFromSubscribeRef.current < MIN_RELOAD_DEBOUNCE_MS) return;
      lastLoadFromSubscribeRef.current = now;
    }
    loadInProgressRef.current = true;
    try {
      setLoading(true);
      
      const user = getUserType();
      let response;
      let countResponse;
      
      if (user.type === 'membre') {
        response = await getMembreNotifications(user.data.numero_matricule);
        countResponse = await getMembreNotificationsCount(user.data.numero_matricule);
      } else if (user.type === 'recolteur') {
        response = await getRecolteurNotifications();
        countResponse = await getRecolteurNotificationsCount();
      } else if (user.type === 'admin') {
        // Une seule requête pour admin (évite 2x les appels = moins de 429)
        response = await getNotifications();
        countResponse = null; // On calcule le count depuis response
      } else if (user.type === 'finances' || user.type === 'gerer') {
        // Les finances et gestionnaires n'ont pas de notifications pour le moment
        setNotifications([]);
        setUnreadCount(0);
        return;
      } else {
        // Aucun utilisateur connecté
        setNotifications([]);
        setUnreadCount(0);
        return;
      }
      
      // Gérer différentes structures de réponse
      let apiNotifications = [];
      if (response && response.data) {
        if (Array.isArray(response.data)) {
          apiNotifications = response.data;
        } else if (response.data.notifications && Array.isArray(response.data.notifications)) {
          apiNotifications = response.data.notifications;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          apiNotifications = response.data.data;
        }
      } else if (Array.isArray(response)) {
        apiNotifications = response;
      }
      
      // Convertir les notifications de l'API au format attendu
      const formattedNotifications = Array.isArray(apiNotifications) ? apiNotifications.map(notif => ({
        id: notif.id,
        type: getNotificationType(notif.type),
        title: notif.titre,
        message: notif.message,
        read: notif.lu,
        timestamp: notif.created_at,
        membre: notif.membre,
        action_par_nom: notif.action_par_nom
      })) : [];

      setNotifications(formattedNotifications);
      
      // Compteur non lues : depuis countResponse ou calculé depuis la liste (admin)
      let unreadCountValue = 0;
      if (countResponse && countResponse.data) {
        if (typeof countResponse.data === 'number') {
          unreadCountValue = countResponse.data;
        } else if (countResponse.data.count !== undefined) {
          unreadCountValue = countResponse.data.count;
        } else if (countResponse.data.nonLues !== undefined) {
          unreadCountValue = countResponse.data.nonLues;
        } else if (countResponse.data.data && countResponse.data.data.count !== undefined) {
          unreadCountValue = countResponse.data.data.count;
        }
      } else {
        // Admin : calculer depuis la liste (évite un 2e appel API)
        unreadCountValue = formattedNotifications.filter(n => !n.read).length;
      }
      setUnreadCount(unreadCountValue);
      retryCountRef.current = 0;  // Reset après succès
    } catch (error) {
      const is429 = error.response?.status === 429;
      const isNetworkError = error.code === 'ERR_NETWORK' || error.message === 'Network Error' || error.code?.includes('QUIC') || error.message?.includes('QUIC');
      // 429 ou erreur réseau/QUIC : backoff progressif, un seul réessai planifié
      if (is429 || isNetworkError) {
        retryCountRef.current = Math.min(retryCountRef.current + 1, 5);
        let delay = RETRY_DELAY_BASE_MS * retryCountRef.current;
        const retryAfter = error.response?.headers?.['retry-after'];
        if (retryAfter) {
          const sec = parseInt(retryAfter, 10);
          if (!isNaN(sec)) delay = Math.max(delay, sec * 1000);
        }
        if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = setTimeout(() => {
          retryTimeoutRef.current = null;
          loadNotifications();
        }, delay);
        return;
      }
      if (error.response?.status === 403) {
        setNotifications([]);
        setUnreadCount(0);
        return;
      }
      setNotifications([]);
      setUnreadCount(0);
    } finally {
      setLoading(false);
      loadInProgressRef.current = false;
    }
  }, [getUserType]);

  useEffect(() => {
    // Charger les notifications au montage
    loadNotifications();

    // S'abonner aux nouvelles notifications locales (rechargement débouncé pour éviter 429)
    const unsubscribe = notificationService.subscribe(() => {
      loadNotifications(true);
    });

    // Polling uniquement quand l'onglet est visible (réduit requêtes et erreurs QUIC/429)
    const tick = () => {
      if (typeof document !== 'undefined' && !document.hidden) {
        loadNotifications();
      }
    };
    pollingIntervalRef.current = setInterval(tick, POLL_INTERVAL_MS);

    return () => {
      unsubscribe();
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
    };
  }, [loadNotifications]);

  // Fermer le panneau si on clique en dehors
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (panelRef.current && !panelRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      // Utiliser capture phase pour éviter les problèmes avec les extensions
      document.addEventListener('mousedown', handleClickOutside, true);
      document.addEventListener('touchstart', handleClickOutside, true);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside, true);
      document.removeEventListener('touchstart', handleClickOutside, true);
    };
  }, [isOpen]);

  const togglePanel = async () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      // Marquer toutes les notifications comme lues quand on ouvre le panneau
      await markAllAsRead();
    }
  };

  const markAllAsRead = async () => {
    try {
      const user = getUserType();
      
      if (user.type === 'membre') {
        // C'est un membre, utiliser la route publique
        await marquerToutesMembreNotificationsLues(user.data.numero_matricule);
      } else if (user.type === 'recolteur') {
        // C'est un récolteur, utiliser la route récolteur
        await marquerToutesRecolteurNotificationsLues();
      } else if (user.type === 'admin') {
        // C'est un admin, utiliser la route admin
        await marquerToutesLues();
      } else if (user.type === 'finances' || user.type === 'gerer') {
        // Pas de notifications pour ces types
        return;
      }
      
      setNotifications((prev) => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Erreur lors du marquage des notifications comme lues:', error);
    }
  };

  const markAsRead = async (id) => {
    try {
      const user = getUserType();
      
      if (user.type === 'membre') {
        // C'est un membre, utiliser la route publique
        await marquerMembreNotificationLue(user.data.numero_matricule, id);
      } else if (user.type === 'recolteur') {
        // C'est un récolteur, utiliser la route récolteur
        await marquerRecolteurNotificationLue(id);
      } else if (user.type === 'admin') {
        // C'est un admin, utiliser la route admin
        await marquerNotificationLue(id);
      } else if (user.type === 'finances' || user.type === 'gerer') {
        // Pas de notifications pour ces types
        return;
      }
      
      setNotifications((prev) => {
        const updated = prev.map(n => n.id === id ? { ...n, read: true } : n);
        setUnreadCount(updated.filter(n => !n.read).length);
        return updated;
      });
    } catch (error) {
      console.error('Erreur lors du marquage de la notification comme lue:', error);
    }
  };

  const clearAll = () => {
    setNotifications([]);
    setUnreadCount(0);
  };

  const getIcon = (type) => {
    switch (type) {
      case 'success':
        return '✓';
      case 'error':
        return '✗';
      case 'warning':
        return '⚠';
      case 'info':
        return 'ℹ';
      default:
        return 'ℹ';
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'À l\'instant';
    if (minutes < 60) return `Il y a ${minutes} min`;
    if (hours < 24) return `Il y a ${hours}h`;
    if (days < 7) return `Il y a ${days}j`;
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const formatNotificationMessage = (notification) => {
    let message = notification.message;
    if (notification.membre) {
      // Remplacer les références au membre si nécessaire
      message = message.replace(
        /Membre/g, 
        `${notification.membre.nom} ${notification.membre.prenom}`
      );
    }
    return message;
  };

  return (
    <div className="notification-bell-container" ref={panelRef}>
      <button 
        className="notification-bell-btn" 
        onClick={togglePanel}
        aria-label="Notifications"
      >
        <span className="bell-icon">🔔</span>
        {unreadCount > 0 && (
          <span className="notification-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
        )}
      </button>

      {isOpen && (
        <div className="notification-panel">
          <div className="notification-panel-header">
            <h3>Notifications</h3>
            <div className="notification-panel-actions">
              {unreadCount > 0 && (
                <button 
                  className="mark-all-read-btn" 
                  onClick={markAllAsRead}
                  title="Marquer tout comme lu"
                >
                  Tout marquer comme lu
                </button>
              )}
              {notifications.length > 0 && (
                <button 
                  className="clear-all-btn" 
                  onClick={clearAll}
                  title="Effacer tout"
                >
                  Effacer
                </button>
              )}
            </div>
          </div>

          <div className="notification-panel-body">
            {loading ? (
              <div className="no-notifications">
                <span className="no-notifications-icon">⏳</span>
                <p>Chargement...</p>
              </div>
            ) : notifications.length === 0 ? (
              <div className="no-notifications">
                <span className="no-notifications-icon">🔕</span>
                <p>Aucune notification</p>
              </div>
            ) : (
              <div className="notifications-list">
                {notifications.map((notification) => (
                  <div
                    key={notification.id}
                    className={`notification-item notification-item-${notification.type} ${!notification.read ? 'unread' : ''}`}
                    onClick={() => markAsRead(notification.id)}
                  >
                    <div className="notification-item-icon">
                      {getIcon(notification.type)}
                    </div>
                    <div className="notification-item-content">
                      <div className="notification-item-title">{notification.title}</div>
                      {notification.message && (
                        <div className="notification-item-message">
                          {formatNotificationMessage(notification)}
                        </div>
                      )}
                      {notification.action_par_nom && (
                        <div className="notification-item-action" style={{ 
                          fontSize: '11px', 
                          color: '#666', 
                          marginTop: '4px',
                          fontStyle: 'italic'
                        }}>
                          Par: {notification.action_par_nom}
                        </div>
                      )}
                      <div className="notification-item-time">
                        {formatTime(notification.timestamp)}
                      </div>
                    </div>
                    {!notification.read && <div className="notification-item-dot"></div>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;

